(() => {
  const controllers = new Map();
  const initialize = (scope = document) => {
    const stores = [...scope.querySelectorAll('[data-budget-collection]')];
    if (scope.matches?.('[data-budget-collection]')) stores.unshift(scope);
    stores.forEach(store => {
      if (controllers.has(store) || !store.dataset.budgetCollection) return;
      const grid = store.querySelector('#d2-featured');
      const budget = store.querySelector('.d2-budget');
      const buttons = [...(budget?.querySelectorAll('button') || [])];
      const more = store.querySelector('[data-d2-more]');
      const moreWrap = store.querySelector('[data-d2-more-wrap]');
      const empty = store.querySelector('[data-d2-empty]');
      const count = store.querySelector('[data-d2-count]');
      const feedback = store.querySelector('[data-budget-feedback]');
      if (!grid || !more || !moreWrap || !empty || !count || !feedback) return;
      const listeners = new AbortController();
      const cache = new Map();
      let request;
      let retry;
      let sequence = 0;
      let current = { key: 'all', min: 0, max: 0 };
      let displayed = grid.querySelectorAll('.d2-product-card').length;
      let shownCards = [...grid.querySelectorAll('.d2-product-card')];
      const curatedAll = store.hasAttribute('data-budget-curated') ? [...shownCards] : [];
      if (curatedAll.length) cache.set('all', { cards: curatedAll, done: true });
      const arrange = (band, length) => {
        grid.toggleAttribute('data-budget-price-order', band.key !== 'all');
        grid.style.setProperty('--budget-columns', String(Math.max(1, Math.ceil(length / 2))));
      };
      let shapes = [];
      let pendingScroll;
      const skeletonObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
        entries.forEach(entry => entry.target.classList.toggle('is-visible', entry.isIntersecting));
      }) : null;
      const pauseSkeletons = () => store.toggleAttribute('data-budget-paused', document.hidden);
      document.addEventListener('visibilitychange', pauseSkeletons, { signal: listeners.signal });
      pauseSkeletons();
      const measureCard = card => {
        const bounds = card.getBoundingClientRect();
        const bones = [];
        const add = (rect, kind, inset = 0) => {
          if (!rect.width || !rect.height) return;
          bones.push({ kind, left: (rect.left - bounds.left) / bounds.width * 100,
            top: (rect.top - bounds.top + inset) / bounds.height * 100,
            width: rect.width / bounds.width * 100, height: (rect.height - inset * 2) / bounds.height * 100 });
        };
        const boxes = (selector, kind) => card.querySelectorAll(selector).forEach(element => add(element.getBoundingClientRect(), kind));
        const text = selector => card.querySelectorAll(selector).forEach(element => {
          const range = document.createRange();
          range.selectNodeContents(element);
          const lines = [];
          for (const rect of range.getClientRects()) {
            const line = lines.find(row => Math.abs(row.top - rect.top) < 2 && Math.abs(row.height - rect.height) < 2);
            if (line) { line.left = Math.min(line.left, rect.left); line.right = Math.max(line.right, rect.right); line.width = line.right - line.left; }
            else lines.push({ top: rect.top, left: rect.left, right: rect.right, width: rect.width, height: rect.height });
          }
          lines.forEach(rect => add(rect, 'text', Math.min(3, rect.height * .15)));
        });
        boxes('.d2-product-card__media', 'image');
        boxes('.rdb-image-label', 'tag');
        boxes('.rosarium-wishlist-action__icon, .d2-product-card__delivery > svg', 'round');
        text('h3 a, .d2-price > span, .d2-price > strong, .d2-product-card__delivery > span, .rdb-rating__average, .rdb-rating__count');
        boxes('.rdb-rating__stars', 'text');
        return { ratio: `${bounds.width} / ${bounds.height}`, bones };
      };
      const clearSkeletons = () => {
        skeletonObserver?.disconnect();
        grid.querySelectorAll('.d2-product-card--skeleton').forEach(card => card.remove());
      };
      const showSkeletons = (append, band) => {
        const samples = [...grid.querySelectorAll('.d2-product-card[data-product-handle]')].slice(0, 12);
        if (samples.length) shapes = samples.map(measureCard);
        if (!shapes.length) return;
        clearSkeletons();
        const placeholders = Array.from({ length: 12 }, (_, index) => {
          const shape = shapes[index % shapes.length];
          const card = document.createElement('div');
          card.className = 'd2-product-card d2-product-card--skeleton';
          card.setAttribute('aria-hidden', 'true');
          card.inert = true;
          card.style.setProperty('--skeleton-ratio', shape.ratio);
          shape.bones.forEach(bone => {
            const part = document.createElement('span');
            part.className = `d2-skeleton-bone d2-skeleton-bone--${bone.kind}`;
            for (const key of ['left', 'top', 'width', 'height']) part.style[key] = `${bone[key]}%`;
            card.append(part);
          });
          return card;
        });
        grid.replaceChildren(...(append ? shownCards : []), ...placeholders);
        arrange(band, (append ? shownCards.length : 0) + placeholders.length);
        grid.dispatchEvent(new CustomEvent('rdb:products-filtered', { bubbles: true }));
        if (append) {
          const card = placeholders[0].getBoundingClientRect(), rail = grid.getBoundingClientRect();
          const offset = getComputedStyle(grid).direction === 'rtl' ? card.right - rail.right + 8 : card.left - rail.left - 8;
          grid.scrollBy({ left: offset, behavior: 'instant' });
        }
        placeholders.forEach(card => skeletonObserver?.observe(card));
      };
      const sortForBand = band => band.key === 'all' ? 'created-descending' : 'price-ascending';
      const endpoint = (band, page = 1) => {
        const url = new URL(store.dataset.budgetCollection, location.origin);
        url.search = '';
        url.searchParams.set('section_id', 'rdb-budget-products');
        url.searchParams.set('sort_by', sortForBand(band));
        if (band.min) url.searchParams.set('filter.v.price.gte', (band.min / 100).toFixed(2));
        if (band.max) url.searchParams.set('filter.v.price.lte', ((band.max - 1) / 100).toFixed(2));
        if (page > 1) url.searchParams.set('page', String(page));
        const preview = new URL(location.href).searchParams.get('preview_theme_id') ||
          (window.Shopify?.theme?.role === 'unpublished' ? window.Shopify.theme.id : '');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        return url;
      };
      const selected = (band = current) => buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.band === band.key)));
      const load = async (band, target, signal) => {
        let entry = cache.get(band.key);
        if (!entry) {
          entry = { cards: [], handles: new Set(), page: 1, done: false };
          cache.set(band.key, entry);
        }
        while (entry.cards.length <= target && !entry.done) {
          const response = await fetch(endpoint(band, entry.page), { credentials: 'same-origin', signal });
          if (!response.ok) throw new Error('Budget response failed');
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
          const result = parsed.querySelector('[data-budget-results]');
          const expectedHandle = store.dataset.budgetCollectionHandle ||
            new URL(store.dataset.budgetCollection, location.origin).pathname.split('/').filter(Boolean).at(-1);
          if (!result || result.dataset.collection !== expectedHandle || result.dataset.sort !== sortForBand(band) ||
              Number(result.dataset.page) !== entry.page || Number(result.dataset.min) !== band.min ||
              Number(result.dataset.max) !== (band.max ? band.max - 1 : 0)) throw new Error('Budget response mismatch');
          const nextPage = result.dataset.next ? Number(new URL(result.dataset.next, response.url).searchParams.get('page')) : 0;
          if (result.dataset.next && nextPage <= entry.page) throw new Error('Budget pagination did not advance');
          for (const item of result.querySelectorAll('[data-budget-item]')) {
            const card = item.querySelector('.d2-product-card');
            const price = Number(card?.dataset.price);
            if (!card || !card.dataset.productHandle || !Number.isFinite(price) || price < band.min || (band.max && price >= band.max)) throw new Error('Budget card mismatch');
            if (entry.handles.has(card.dataset.productHandle)) continue;
            card.dataset.budgetCreated = item.dataset.created;
            entry.handles.add(card.dataset.productHandle);
            entry.cards.push(card);
          }
          entry.done = !nextPage;
          entry.page = nextPage || entry.page;
        }
        return entry;
      };
      const show = async (band, append = false) => {
        request?.abort();
        retry = null;
        request = new AbortController();
        const signal = request.signal;
        const ticket = ++sequence;
        const target = band.key === 'all' && curatedAll.length ? curatedAll.length : (append ? displayed + 12 : 12);
        const previousCount = append ? displayed : 0;
        const previousScroll = pendingScroll ?? grid.scrollLeft;
        pendingScroll = previousScroll;
        grid.setAttribute('aria-busy', 'true');
        more.disabled = true;
        selected(band);
        empty.hidden = true;
        feedback.classList.add('visually-hidden');
        feedback.textContent = feedback.dataset.loading;
        feedback.hidden = false;
        const cached = cache.get(band.key);
        if (!cached || (!cached.done && cached.cards.length <= target)) showSkeletons(append, band);
        try {
          const entry = await load(band, target, signal);
          if (ticket !== sequence || !store.isConnected) return;
          current = band;
          const cards = entry.cards.slice(0, target);
          clearSkeletons();
          grid.replaceChildren(...cards);
          arrange(band, cards.length);
          grid.toggleAttribute('data-budget-curated-active', band.key === 'all' && curatedAll.length > 0);
          shownCards = cards;
          displayed = cards.length;
          selected();
          empty.hidden = displayed > 0;
          moreWrap.hidden = entry.done && entry.cards.length <= displayed;
          count.textContent = count.dataset.template.replace('__COUNT__', String(displayed));
          grid.dispatchEvent(new CustomEvent('rdb:products-filtered', { bubbles: true }));
          if (append) {
            grid.scrollTo({ left: previousScroll, behavior: 'instant' });
            cards[previousCount]?.querySelector('h3 a')?.focus({ preventScroll: true });
          }
          feedback.hidden = true;
        } catch (error) {
          if (error.name !== 'AbortError' && ticket === sequence) {
            clearSkeletons();
            grid.replaceChildren(...shownCards);
            arrange(current, shownCards.length);
            selected();
            empty.hidden = shownCards.length > 0;
            grid.dispatchEvent(new CustomEvent('rdb:products-filtered', { bubbles: true }));
            grid.scrollTo({ left: previousScroll, behavior: 'instant' });
            retry = { band, append };
            moreWrap.hidden = false;
            feedback.classList.remove('visually-hidden');
            feedback.textContent = feedback.dataset.error;
            feedback.hidden = false;
          }
        } finally {
          if (ticket === sequence) {
            pendingScroll = undefined;
            grid.removeAttribute('aria-busy');
            more.disabled = false;
          }
        }
      };
      buttons.forEach(button => button.addEventListener('click', () => show({
        key: button.dataset.band, min: Number(button.dataset.min), max: Number(button.dataset.max)
      }), { signal: listeners.signal }));
      more.addEventListener('click', () => retry ? show(retry.band, retry.append) : show(current, true), { signal: listeners.signal });
      if (budget) budget.hidden = false;
      moreWrap.hidden = curatedAll.length > 0 || Number(store.dataset.budgetTotal) <= displayed;
      empty.hidden = displayed > 0;
      count.textContent = count.dataset.template.replace('__COUNT__', String(displayed));
      grid.dispatchEvent(new CustomEvent('rdb:products-filtered', { bubbles: true }));
      store.dataset.budgetReady = 'true';
      controllers.set(store, () => {
        request?.abort();
        sequence++;
        clearSkeletons();
        grid.replaceChildren(...shownCards);
        arrange(current, shownCards.length);
        grid.removeAttribute('aria-busy');
        more.disabled = false;
        feedback.hidden = true;
        selected();
        listeners.abort();
        controllers.delete(store);
      });
    });
  };
  initialize();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [store, cleanup] of controllers) if (event.target.contains(store)) cleanup();
  });
})();
